/**
 * JoinCodePage (WU-19, RolesUsersPlan §4.8) — join by the 8-digit code
 * (/p/join). Eight numeric boxes → POST /public/join/code (preview) → the same
 * preview card + claim form as JoinPage → POST /public/join/code/claim →
 * "Open the app". The browser is never signed in.
 *
 * Boxes: typing a digit advances; Backspace steps back; pasting (or a
 * programmatic fill of) a whole code into any box fills all eight.
 */
import React, { useRef, useState } from 'react';
import type { JoinClaimResponse, JoinPreview } from '@buildledger/shared';

import {
  isUnusableError,
  joinErrorMessage,
  postJoinCodeClaim,
  postJoinCodeClaimWithGoogle,
  postJoinCodePreview,
} from '../join-api';
import { JoinClaimForm, JoinPreviewHeader, JoinSuccess, JoinUnusable } from './JoinPage';
import '../join.css';

const CODE_LENGTH = 8;
const EMPTY_DIGITS: readonly string[] = Array.from({ length: CODE_LENGTH }, () => '');

type Step =
  | { kind: 'code' }
  | { kind: 'ready'; code: string; preview: JoinPreview }
  | { kind: 'gone' }
  | { kind: 'claimed'; result: JoinClaimResponse; viaGoogle: boolean };

export function JoinCodePage(): JSX.Element {
  const [step, setStep] = useState<Step>({ kind: 'code' });
  const [digits, setDigits] = useState<string[]>([...EMPTY_DIGITS]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const code = digits.join('');
  const complete = /^\d{8}$/.test(code);

  const startOver = (): void => {
    setDigits([...EMPTY_DIGITS]);
    setError(null);
    setStep({ kind: 'code' });
  };

  if (step.kind === 'gone') return <JoinUnusable via="code" onRetry={startOver} />;
  if (step.kind === 'claimed') {
    return <JoinSuccess result={step.result} viaGoogle={step.viaGoogle} />;
  }

  if (step.kind === 'ready') {
    const readyCode = step.code;
    return (
      <div className="stage stage-center" data-testid="join-code-page">
        <div className="card invite-card">
          <JoinPreviewHeader preview={step.preview} />
          <div className="join-code-line">
            <span>
              Code <strong>{`${readyCode.slice(0, 4)}-${readyCode.slice(4)}`}</strong>
            </span>
            <button className="btn btn-ghost" type="button" onClick={startOver}>
              Use a different code
            </button>
          </div>
          <JoinClaimForm
            via="code"
            submit={(body) => postJoinCodeClaim({ ...body, code: readyCode })}
            submitGoogle={(signupToken, name) =>
              postJoinCodeClaimWithGoogle(readyCode, signupToken, name)
            }
            onClaimed={(result, viaGoogle) => setStep({ kind: 'claimed', result, viaGoogle })}
            onGone={() => setStep({ kind: 'gone' })}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="stage stage-center" data-testid="join-code-page">
      <div className="card invite-card">
        <div className="brand-rule" />
        <p className="invite-kicker">Join with a code</p>
        <h1>Enter your join code</h1>
        <form
          className="invite-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!complete || busy) return;
            setBusy(true);
            setError(null);
            postJoinCodePreview(code)
              .then((preview) =>
                setStep(preview.usable ? { kind: 'ready', code, preview } : { kind: 'gone' }),
              )
              .catch((err: unknown) => {
                if (isUnusableError(err, 'code')) setStep({ kind: 'gone' });
                else setError(joinErrorMessage(err, 'code'));
              })
              .finally(() => setBusy(false));
          }}
        >
          <CodeBoxes
            digits={digits}
            disabled={busy}
            onChange={(next) => {
              setDigits(next);
              setError(null);
            }}
          />
          <p className="muted join-code-hint" id="join-code-hint">
            The 8-digit code looks like 1234-5678. You can paste the whole code.
          </p>
          {error !== null && (
            <p className="form-error" role="alert" data-testid="join-error">
              {error}
            </p>
          )}
          <button
            className="btn btn-primary btn-wide"
            type="submit"
            data-testid="join-submit"
            disabled={!complete || busy}
          >
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

function CodeBoxes({
  digits,
  disabled,
  onChange,
}: {
  digits: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}): JSX.Element {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const focusBox = (index: number): void => {
    const el = refs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
    if (!el) return;
    el.focus();
    el.select();
  };

  const setAt = (index: number, value: string): string[] => {
    const next = [...digits];
    next[index] = value;
    return next;
  };

  /** Spread several digits from `start`; a full code always starts at box 1. */
  const spread = (start: number, incoming: string): void => {
    const from = incoming.length >= CODE_LENGTH ? 0 : start;
    const next = [...digits];
    for (let k = 0; k < incoming.length && from + k < CODE_LENGTH; k += 1) {
      next[from + k] = incoming[k];
    }
    onChange(next);
    focusBox(from + incoming.length);
  };

  return (
    <fieldset className="join-code" disabled={disabled}>
      <legend>Join code</legend>
      <div className="join-code-boxes">
        {digits.map((digit, i) => (
          <React.Fragment key={i}>
            {i === CODE_LENGTH / 2 && (
              <span className="join-code-dash" aria-hidden="true">
                –
              </span>
            )}
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              className="join-code-box"
              data-testid={i === 0 ? 'join-code-input' : undefined}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              aria-describedby="join-code-hint"
              value={digit}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.metaKey || e.ctrlKey || e.altKey) return;
                if (/^\d$/.test(e.key)) {
                  e.preventDefault();
                  onChange(setAt(i, e.key));
                  focusBox(i + 1);
                } else if (e.key === 'Backspace') {
                  e.preventDefault();
                  if (digit !== '') {
                    onChange(setAt(i, ''));
                  } else if (i > 0) {
                    onChange(setAt(i - 1, ''));
                    focusBox(i - 1);
                  }
                } else if (e.key === 'Delete') {
                  e.preventDefault();
                  onChange(setAt(i, ''));
                } else if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  focusBox(i - 1);
                } else if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  focusBox(i + 1);
                }
              }}
              onPaste={(e) => {
                const incoming = e.clipboardData.getData('text').replace(/\D/g, '');
                if (incoming.length === 0) return;
                e.preventDefault();
                spread(i, incoming);
              }}
              onChange={(e) => {
                // Soft keyboards / autofill / programmatic fill land here.
                const incoming = e.target.value.replace(/\D/g, '');
                if (incoming === '') {
                  onChange(setAt(i, ''));
                } else if (incoming.length === 1) {
                  onChange(setAt(i, incoming));
                  focusBox(i + 1);
                } else if (incoming.length === 2 && digit !== '' && incoming.startsWith(digit)) {
                  // Typed after an unselected digit: the new keystroke wins.
                  onChange(setAt(i, incoming[1]));
                  focusBox(i + 1);
                } else {
                  spread(i, incoming);
                }
              }}
            />
          </React.Fragment>
        ))}
      </div>
    </fieldset>
  );
}
